/**
 * Fulfillment Service
 * 
 * Abstracts data fetching for fulfillments. Uses real API endpoints.
 * 
 * Follows Interface Segregation and Dependency Inversion principles.
 */

import type {
  FulfillmentResponse,
  FulfillmentQueryParams,
  FulfillmentUpdateRequest,
  FulfillmentStatusCounts,
} from '@/app/types/fulfillment';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

/**
 * Fulfillment Service Interface
 * Define the contract that any implementation must follow
 */
export interface IFulfillmentService {
  queryFulfillments(
    params: FulfillmentQueryParams
  ): Promise<{ fulfillments: FulfillmentResponse[]; total: number }>;
  
  getFulfillmentById(id: string): Promise<FulfillmentResponse | null>;
  
  updateFulfillment(
    fulfillmentId: string,
    request: FulfillmentUpdateRequest
  ): Promise<FulfillmentResponse | null>;
  
  getStatusCounts(): Promise<FulfillmentStatusCounts>;
}

/**
 * Real API Implementation
 * Calls the admin fulfillment endpoints
 */
const apiFulfillmentService: IFulfillmentService = {
  async queryFulfillments(
    params: FulfillmentQueryParams
  ): Promise<{ fulfillments: FulfillmentResponse[]; total: number }> {
    try {
      // Build query params, filtering out undefined/null values
      const queryParams: Record<string, any> = {};
      if (params.user_id !== undefined && params.user_id !== null) {
        queryParams.user_id = parseInt(params.user_id, 10);
      }
      if (params.reward_id !== undefined && params.reward_id !== null) {
        queryParams.reward_id = params.reward_id;
      }
      if (params.status !== undefined && params.status !== null) {
        queryParams.fulfillment_status = params.status;
      }
      if (params.category !== undefined && params.category !== null) {
        queryParams.category = params.category;
      }
      if (params.from_date !== undefined && params.from_date !== null) {
        queryParams.from_date = params.from_date;
      }
      if (params.to_date !== undefined && params.to_date !== null) {
        queryParams.to_date = params.to_date;
      }
      // Always include limit and offset
      queryParams.limit = params.limit ?? 100;
      queryParams.offset = params.offset ?? 0;

      const response = await api.get<{ fulfillments: FulfillmentResponse[]; total: number }>(
        API_ENDPOINTS.admin.fulfillments,
        { params: queryParams }
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to query fulfillments:', error);
      throw error;
    }
  },

  async getFulfillmentById(id: string): Promise<FulfillmentResponse | null> {
    try {
      const response = await api.get<FulfillmentResponse>(
        API_ENDPOINTS.admin.fulfillmentDetail(id)
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get fulfillment:', error);
      throw error;
    }
  },

  async updateFulfillment(
    fulfillmentId: string,
    request: FulfillmentUpdateRequest
  ): Promise<FulfillmentResponse | null> {
    try {
      const response = await api.patch<FulfillmentResponse>(
        API_ENDPOINTS.admin.fulfillmentDetail(fulfillmentId),
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to update fulfillment:', error);
      throw error;
    }
  },

  async getStatusCounts(): Promise<FulfillmentStatusCounts> {
    try {
      const response = await api.get<FulfillmentStatusCounts>(
        API_ENDPOINTS.admin.fulfillmentStatusCounts
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to get fulfillment status counts:', error);
      throw error;
    }
  },
};

// Export the real API implementation
export const fulfillmentService: IFulfillmentService = apiFulfillmentService;
export default fulfillmentService;

